import React, { useEffect, useState } from 'react';
import { AlertTriangle, Users, Radio } from 'lucide-react';
import {
  tabletopMesAPI,
  tabletopInjectsAPI,
  tabletopPersonasAPI,
  tabletopCoverageAPI,
  TabletopExercise,
  TabletopMasterScenario,
  TabletopInject,
  TabletopPersona,
  TabletopCoverageResult,
} from '../../services/tabletopService';
import CoverageGatePage from './CoverageGatePage';

interface Props {
  projectId: string;
  exercise: TabletopExercise;
  onExerciseChange: (exercise: TabletopExercise) => void;
}

const READINESS_LABEL: Record<string, string> = {
  systems_down: 'Systems-down / plan-accessibility test',
  unsupported_decision: 'Unsupported decision point',
};

const ScenarioReviewPage: React.FC<Props> = ({ projectId, exercise, onExerciseChange }) => {
  const [mes, setMes] = useState<TabletopMasterScenario | null>(null);
  const [injects, setInjects] = useState<TabletopInject[]>([]);
  const [personas, setPersonas] = useState<TabletopPersona[]>([]);
  const [coverage, setCoverage] = useState<TabletopCoverageResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      tabletopMesAPI.get(projectId, exercise.exerciseId),
      tabletopInjectsAPI.list(projectId, exercise.exerciseId),
      tabletopPersonasAPI.list(projectId, exercise.exerciseId),
      tabletopCoverageAPI.get(projectId, exercise.exerciseId),
    ]).then(([mesRes, injectsRes, personasRes, coverageRes]) => {
      setMes(mesRes.data.mes);
      setInjects(injectsRes.data.injects.sort((a, b) => a.seq - b.seq));
      setPersonas(personasRes.data.personas);
      setCoverage(coverageRes.data.coverage);
    }).finally(() => setLoading(false));
  }, [projectId, exercise.exerciseId]);

  if (loading) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">Loading scenario…</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-6">

        <CoverageGatePage
          projectId={projectId}
          exercise={exercise}
          coverage={coverage}
          onApproved={onExerciseChange}
        />

        {mes && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6">
            <h2 className="text-sm font-bold text-navy mb-2">Master Exercise Scenario — organiser summary</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{mes.summary}</p>
            <p className="text-xs text-slate-400 mt-3 italic">Not shown to participants directly — revealed only through injects.</p>
          </div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-2">
            <Radio size={16} className="text-primary" />
            <h2 className="text-sm font-bold text-navy">Injects ({injects.length})</h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {injects.map(inject => (
              <div key={inject.seq} className="px-6 py-3 flex items-start gap-3">
                <span className="text-xs font-mono text-slate-400 w-8 flex-shrink-0 mt-0.5">#{inject.seq}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-navy">{inject.content}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-slate-400 uppercase tracking-wide">{inject.channel}</span>
                    {inject.isMandatoryReadinessInject && (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        <AlertTriangle size={11} /> {READINESS_LABEL[inject.isMandatoryReadinessInject]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {injects.length === 0 && <p className="px-6 py-8 text-sm text-slate-400 text-center">No injects yet.</p>}
          </div>
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-2">
            <Users size={16} className="text-primary" />
            <h2 className="text-sm font-bold text-navy">Cast ({personas.length})</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {personas.map(persona => (
              <div key={persona.personaId} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-navy">{persona.displayName}</p>
                  <p className="text-xs text-slate-400">{persona.organisation} · {persona.category.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{persona.control.replace(/_/g, ' ')}</span>
                  <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{persona.deliveryChannel.replace(/_/g, ' ')}</span>
                </div>
              </div>
            ))}
            {personas.length === 0 && <p className="px-6 py-8 text-sm text-slate-400 text-center">No cast yet.</p>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ScenarioReviewPage;
